import React, { lazy, Suspense, useCallback } from 'react';
import { createPortal } from 'react-dom';

import { Pencil, Trash2 } from 'lucide-react';

import { PersonalisationLoader } from '../PersonalisationLoader';
import { useViewModel } from './useViewModel';
import { isValidHexColour } from './utils/engravingTint';
import type { PhotoEditorProps } from './types';
import { ImagePreview, FileInput, UploadButton, NoFileChosen } from './ui/renderHelpers';
import { CloseConfirm } from './ui/CloseConfirm';
import { PhotoEditorControls } from './ui/PhotoEditorControls';
import { useEditorChrome } from './hooks/useEditorChrome';
import { PersonalisationHeader } from '../PersonalisationHeader';
import { PersonalisationTotal } from '../PersonalisationFooter/PersonalisationTotal';

import themeStyles from './photoEditorTheme.module.css';

const DoodleCanvasLazy = lazy(
  () =>
    import('./ui/DoodleCanvas').then((module) => ({ default: module.DoodleCanvas })) as Promise<{
      default: React.ComponentType<any>;
    }>
);

/**
 * PhotoEditor Component
 *
 * A simple photo editing component that provides:
 * - Image upload functionality
 * - Full-screen modal editor
 * - Rotate, flip, and zoom capabilities
 * - Blob output for processed images
 */
export const PhotoEditor = (props: PhotoEditorProps) => {
  const {
    stepImageUrl,
    uploadType,
    className,
    accept,
    disabled,
    id,
    label,
    showStarMapSlot = false,
    showLocationMapSlot = false,
    maskZones,
    hideMaskZoneButtons = false,
    doodle = false,
    textColour
  } = props;
  const isEngraving = uploadType === 'engraving';
  const isMapSlot = showStarMapSlot || showLocationMapSlot;
  // When the slot configures an engraving colour, the canvas preview is tinted
  // (colour strokes on transparency), so the multiply blend and contrast filter
  // must stay off or they would darken/greyscale the colour away. Doodle mode
  // shows the live drawing canvas (black strokes on white) instead of the tinted
  // preview, so it keeps the multiply blend; doodles are tinted on save.
  const previewIsTinted = isEngraving && !isMapSlot && !doodle && isValidHexColour(textColour);
  // For star map and location map, apply blend mode but not contrast.
  const shouldApplyBlendMode = (isEngraving || isMapSlot) && !previewIsTinted;
  const shouldApplyContrast = isEngraving && !isMapSlot && !previewIsTinted;
  const isMultiZoneMode = maskZones && maskZones.length > 0;

  const {
    // State
    isEditorOpen,
    selectedImage,
    imageUrl,
    scale,
    flipX,
    error,
    isLoading,
    isProcessing,
    canvasPreview,
    isGeneratingPreview,
    isMaskReady,

    // Multi-zone state
    zones,
    zoneImages,
    activeZoneIndex,
    allZonesHaveFiles,
    fileInputRefs,
    activeZoneGlow,

    // Doodle state
    strokeWidth,
    doodleCanvasDimensions,
    doodleCanvasRef,

    // Refs
    fileInputRef,
    stepImageRef,
    rootRef,

    // Close confirmation
    isCloseConfirmOpen,
    showEditorDialog,
    handleDialogCancel,
    handleDialogClose,
    handleKeepEditing,
    handleConfirmClose,

    // Handlers
    handleFileSelect,
    handleSave,
    handleClose,
    handleClear,
    handleButtonClick,
    handleStepImageLoad,
    handleStepImageError,
    handleZoneButtonClick,
    handleStrokeWidthChange,
    handleDoodleClear,

    // Editor handlers
    handleRotateLeft,
    handleRotateRight,
    handleFlipX,
    handleZoomOut,
    handleZoomIn,
    handleMoveLeft,
    handleMoveRight,
    handleMoveUp,
    handleMoveDown,
    handleResetAll,

    // Computed values
    buttonClasses,
    zoneButtonClasses
  } = useViewModel(props);
  const chrome = useEditorChrome();

  // Stable callback ref: an inline ref would re-run on every render and the
  // eager .complete check would then loop renders via setStepImageDimensions.
  // A cached image can fire `load` before React attaches onLoad, so read the
  // dimensions eagerly if the image is already complete.
  const setStepImageRef = useCallback(
    (el: HTMLImageElement | null) => {
      stepImageRef.current = el;
      if (el?.complete && el.naturalWidth > 0) {
        handleStepImageLoad();
      }
    },
    [stepImageRef, handleStepImageLoad]
  );

  // Create portal for full-screen modal using HTML dialog element
  const modalPortal = isEditorOpen
    ? createPortal(
        <dialog
          ref={(dialog) => {
            if (dialog && isEditorOpen && !dialog.open) {
              showEditorDialog(dialog);
            }
          }}
          onCancel={handleDialogCancel}
          onClose={handleDialogClose}
          className={themeStyles['photo-editor__modal']}
          style={{
            // Safari-specific fix for proper container sizing; the width and the desktop layout
            // come from the CSS.
            minHeight: '100dvh'
          }}
        >
          <PersonalisationLoader isVisible={isProcessing} />

          <PersonalisationHeader
            title="Edit Photo"
            resetText={doodle ? null : 'Reset'}
            onReset={handleResetAll}
            onClose={handleClose}
            onBack={handleClose}
            closeLabel="Cancel & Close"
            totalSteps={chrome.totalSteps}
            currentStep={chrome.currentStep}
            isResetting={false}
            headerStyle={chrome.headerStyle}
            showProgress={false}
            disabled={isProcessing}
          />
          <div className={themeStyles['photo-editor__modal-content']}>
            {/* Image Preview Area */}
            <div className={themeStyles['photo-editor__image-area']} id="photo-editor-canvas-container">
              <div
                id="photo-editor-canvas"
                className={themeStyles['photo-editor__canvas-container']}
                style={{
                  position: 'relative',
                  width: '100%',
                  height: '100%',
                  // Figma's picture is 200px of an 812px screen (about 25dvh), which leaves room for both
                  // control cards and the footer (the middle scrolls on short screens). Doodle is exempt:
                  // its stage size sets the saved drawing's resolution. From 768px the CSS sets
                  // --photo-editor-picture-max for both, as the picture has the left of the screen to itself.
                  maxWidth: doodle
                    ? 'var(--photo-editor-picture-max, 100%)'
                    : 'var(--photo-editor-picture-max, min(100%, 25dvh))',
                  maxHeight: '100%',
                  margin: 'auto'
                }}
              >
                {/* Step Image Background - absolutely positioned but with zIndex: 1 */}
                <div style={{ position: 'relative' }}>
                  {stepImageUrl && (
                    <img
                      ref={setStepImageRef}
                      src={stepImageUrl}
                      alt="Step background"
                      className={themeStyles['photo-editor__step-background']}
                      onError={handleStepImageError}
                      onLoad={handleStepImageLoad}
                      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    />
                  )}

                  {/* Canvas Preview - Uses same logic as save handlers */}
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      bottom: 0,
                      right: 0,
                      zIndex: 10,
                      mixBlendMode: shouldApplyBlendMode ? 'multiply' : 'normal',
                      // Safari-specific fixes for proper container sizing
                      width: '100%',
                      height: '100%'
                    }}
                  >
                    <div
                      id="photo-editor-image-container"
                      style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                        // Safari-specific fixes for container expansion
                        minWidth: '100%',
                        minHeight: '100%',
                        mixBlendMode: shouldApplyBlendMode ? 'multiply' : 'normal',
                        // Hide mask until it's in final position
                        opacity: isMaskReady ? 1 : 0,
                        transition: 'opacity 0.2s ease-in-out'
                      }}
                    >
                      {/* Doodle mode: render DoodleCanvas for free drawing */}
                      {doodle ? (
                        <Suspense fallback={<div>Loading canvas...</div>}>
                          <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                            {doodleCanvasDimensions.width > 0 && doodleCanvasDimensions.height > 0 ? (
                              <DoodleCanvasLazy
                                ref={doodleCanvasRef}
                                width={doodleCanvasDimensions.width}
                                height={doodleCanvasDimensions.height}
                                strokeWidth={strokeWidth}
                                initialImageUrl={imageUrl || undefined}
                              />
                            ) : (
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: '100%',
                                  height: '100%',
                                  color: '#666',
                                  fontSize: '14px'
                                }}
                              >
                                Loading canvas...
                              </div>
                            )}
                          </div>
                        </Suspense>
                      ) : (isMultiZoneMode ? zoneImages.size > 0 : imageUrl) ? (
                        <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                          {canvasPreview ? (
                            <img
                              id="photo-editor-canvas-preview"
                              src={canvasPreview}
                              alt="Canvas Preview"
                              className={themeStyles['photo-editor__canvas-preview']}
                              style={{
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: 'contain',
                                display: 'block',
                                mixBlendMode: shouldApplyBlendMode ? 'multiply' : 'normal',
                                filter: shouldApplyContrast ? 'grayscale(100%) contrast(300%)' : 'none'
                              }}
                            />
                          ) : (
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '100%',
                                height: '100%',
                                color: '#666',
                                fontSize: '14px',
                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                borderRadius: '8px'
                              }}
                            >
                              {isGeneratingPreview ? 'Generating canvas preview...' : 'Loading canvas preview...'}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '100%',
                            height: '100%',
                            color: '#999',
                            fontSize: '14px'
                          }}
                        >
                          {isMultiZoneMode ? 'Upload images for all zones to see preview' : 'No image selected'}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Controls Panel */}
            <div className={themeStyles['photo-editor__controls-panel']}>
              {!doodle && (
                <PhotoEditorControls
                  scale={scale}
                  flipX={flipX}
                  showZoom={!showLocationMapSlot}
                  showFlip={!showLocationMapSlot}
                  onMoveLeft={handleMoveLeft}
                  onMoveRight={handleMoveRight}
                  onMoveUp={handleMoveUp}
                  onMoveDown={handleMoveDown}
                  onZoomIn={handleZoomIn}
                  onZoomOut={handleZoomOut}
                  onRotateLeft={handleRotateLeft}
                  onRotateRight={handleRotateRight}
                  onFlip={handleFlipX}
                />
              )}

              {/* Doodle Controls - only show in doodle mode */}
              {doodle && (
                <div className={themeStyles['photo-editor__card'] + ' ' + themeStyles['photo-editor__card--doodle']}>
                  <div className={themeStyles['photo-editor__split-row-column']}>
                    <div className={themeStyles['photo-editor__label'] + ' body-xs body-uppercase font-medium'}>
                      <Pencil size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                      Brush Size ({strokeWidth}px)
                    </div>
                    <div className={themeStyles['photo-editor__doodle-slider-container']}>
                      <input
                        type="range"
                        min="1"
                        max="20"
                        value={strokeWidth}
                        onChange={(e) => handleStrokeWidthChange(Number(e.target.value))}
                        className={themeStyles['photo-editor__doodle-slider']}
                        aria-label="Brush size"
                      />
                    </div>
                  </div>
                  <div className={themeStyles['photo-editor__split-row-column']}>
                    <div className={themeStyles['photo-editor__label'] + ' body-xs body-uppercase font-medium'}>
                      Clear
                    </div>
                    <div className={themeStyles['photo-editor__button-row']}>
                      <button
                        type="button"
                        className="button button--secondary button--black button--small"
                        onClick={handleDoodleClear}
                        aria-label="Clear drawing"
                      >
                        <Trash2 />
                        Clear
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className={themeStyles['photo-editor__modal-footer']}>
            {/* Zone buttons - only show in multi-zone mode when colour picking is allowed */}
            {isMultiZoneMode && !hideMaskZoneButtons && zones.length > 0 && (
              <div className={themeStyles['photo-editor__zone-buttons']}>
                {zones.map((zone, index) => (
                  <button
                    key={index}
                    type="button"
                    className={zoneButtonClasses(index)}
                    onClick={() => handleZoneButtonClick(index)}
                    disabled={isProcessing}
                    aria-label={`Select ${zone.zoneLabel} zone`}
                  >
                    {zone.zoneLabel}
                  </button>
                ))}
              </div>
            )}
            <PersonalisationTotal price={chrome.totalPrice} />
            <button
              onClick={handleSave}
              className={themeStyles['photo-editor__continue'] + ' button button--primary button--orange'}
              type="button"
              disabled={isProcessing}
            >
              Continue
            </button>
          </div>
          {isCloseConfirmOpen && <CloseConfirm onKeepEditing={handleKeepEditing} onConfirmClose={handleConfirmClose} />}
        </dialog>,
        document.getElementById('photo-editor-portal') || document.body
      )
    : null;

  return (
    <div ref={rootRef} className={`${themeStyles['photo-editor']} ${className}`}>
      {isMultiZoneMode ? (
        /* Multi-zone mode: render file inputs for each zone */
        <>
          {zones.map((zone, index) => {
            const zoneImage = zoneImages.get(index);
            const showGreenTick = fileInputRefs[index]?.current?.files?.length > 0;
            const buttonLabel = `${label} - ${zone.zoneLabel}`;

            return (
              <div key={index} className={themeStyles['photo-editor__zone-input-wrapper']}>
                {zoneImage && (
                  <ImagePreview
                    imageUrl={zoneImage.blobUrl}
                    filename={zoneImage.file.name}
                    onRemove={() => handleClear(index)}
                  />
                )}

                <FileInput
                  inputRef={fileInputRefs[index]}
                  accept={accept}
                  disabled={disabled}
                  id={`${id}-zone-${index}`}
                  ariaLabel={`File input for ${zone.zoneLabel}`}
                  onChange={(e) => handleFileSelect(e, index)}
                />

                <UploadButton
                  label={buttonLabel}
                  buttonClasses={buttonClasses}
                  isLoading={isLoading}
                  disabled={disabled ?? false}
                  onClick={() => handleButtonClick(index)}
                  ariaLabel={`${label ?? ''} for ${zone.zoneLabel}`}
                  showGreenTick={showGreenTick}
                />

                <NoFileChosen hasFile={!!zoneImage} />
              </div>
            );
          })}
        </>
      ) : (
        /* Single zone mode: existing behavior */
        <>
          {imageUrl && selectedImage && (
            <ImagePreview imageUrl={imageUrl} filename={selectedImage.name} onRemove={() => handleClear()} />
          )}

          <FileInput
            inputRef={fileInputRef}
            accept={accept}
            disabled={disabled}
            id={id ?? ''}
            ariaLabel="File input"
            onChange={(e) => handleFileSelect(e, 0)}
          />

          <UploadButton
            label={label ?? ''}
            buttonClasses={buttonClasses}
            isLoading={isLoading}
            disabled={disabled ?? false}
            onClick={() => handleButtonClick()}
            ariaLabel={label ?? ''}
          />

          <NoFileChosen hasFile={!!selectedImage} />
        </>
      )}

      {/* Error message */}
      {error && (
        <div className={themeStyles['photo-editor__error'] + ' body-sm font-medium'} role="alert">
          {error}
        </div>
      )}

      {/* Full Screen Photo Editor Modal */}
      {modalPortal}
    </div>
  );
};
